<template>
  <v-card :max-width="width" :max-height="height" :width="width">
    <v-card-title class="primary">
      <span class="headline white--text">
        {{title}}
      </span>
      <v-spacer/>
      <BaseDialogActions hideRefresh hideFullscreen/>
    </v-card-title>
    <v-card-text>
      <FormWrapper
      testId="deck-form-save-btn"
      @submit="saveChanges">
        <template #left-action>
            <v-row align="center" justify="space-between">
              <v-col cols="auto" v-if="deckCanBeDefault">
                <v-checkbox
                v-model="deck.isDefault"
                :label="$t('isDefault')"
                color="primary"
                class="mr-3"/>
              </v-col>

              <v-col cols="auto">
                <v-checkbox
                v-model="deck.status"
                data-testid="deck-status-check"
                color="primary"
                :label="$t('open')"/>
              </v-col>
            </v-row>
        </template>
        <v-container fluid grid-list-md>
          <v-row dense>
            <v-col cols="12">
              <v-text-field
              ref="firstField"
              color="black"
              data-testid="deck-name"
              :rules="[rules.required]"
              :label="$t('name')"
              v-model="deck.name"
              maxlength="64"
              counter/>
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="deck.contentType"
                :items="contentTypes"
                item-text="name"
                item-value="name"
                :item-disabled="item => item.logYardSaleUnavailable"
                :label="$t('contentType')"
                :readonly="isEditing"
                persistent-hint>
                <template #item="{ item, attrs }">
                  <div v-bind="attrs" style="width: 100%; pointer-events: none;">
                    {{ item.name }}
                    <Icon
                    icon="mdi-truck-delivery-outline"
                    iconColor="error"
                    v-if="item.logYardSaleUnavailable"
                    :tooltipText="$t('onlyOneLogYardSaleDeckPerLocation')"
                    style="pointer-events: auto;"/>
                  </div>
                </template>
              </v-select>
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="deck.consumptionMode"
                :readonly="deck.onDeck > 0 || isByproductContentType"
                :hint="consumptionModeHint"
                :items="consumptionModesAvailable"
                :label="$t('deckUnit')"
                :disabled="consumptionModeDisabled"
                @change="handleConsumptionModeChanged"
                persistent-hint>
                <template #append-outer v-if="showDeckUnitAlert">
                  <Icon
                  icon="mdi-alert"
                  iconColor="error"
                  :tooltipText="speciesAutocompleteHint"
                  :small="false"/>
                </template>
              </v-select>
            </v-col>

            <v-col cols="12" md="6" v-if="!isLogYardSaleContentType">
              <v-text-field
              color="black"
              :label="onDeckLabel"
              :rules="isEditing ? [] : [rules.positiveDecimal, rules.validOnDeckTons]"
              :readonly="isEditing"
              v-model="deck.onDeck"
              />
            </v-col>

            <v-col cols="12" md="6" v-if="!isLogYardSaleContentType">
              <MoneyTextField
                :initialValue="deck.value"
                :readonly="propDeck !== undefined"
                :label="$t('value')"
                @val-changed="deckValueEntered"
              />
            </v-col>

            <v-col cols="12" v-if="showSpecies">
              <LoadableComponent
              :loading="speciesLoading || !speciesFetched || !renderSpeciesAutocomplete">
                <SpeciesAutocomplete
                :initSelection="initialSpeciesSelection"
                :hint="speciesAutocompleteHint"
                :speciesList="filteredSpecies"
                @species-chosen="speciesChosen"
                />
              </LoadableComponent>
            </v-col>
          </v-row>
          <v-row dense v-if="showProductRestrictionOptions">
            <v-col v-if="this.propDeck">
              <DeckProductSelector
                :propProducts="deckProductIds()"
                :includeByproducts="isByproductContentType"
                :includeLogsProducts="!isByproductContentType"
                :locationId="location.locationId"
                :deckId="propDeck.deckId"
                :productIdsOnDeck="deck.productsOnDeck"
                @products-selected="productsSelected"
              />
            </v-col>
            <v-spacer v-else></v-spacer>
          </v-row>
        </v-container>
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import fieldRules from '@/utils/rules'
import { mapActions, mapGetters } from 'vuex'
import { ConsumptionMode, DeckContentType, DeckStatus } from '@/utils/Enumerations.js'
import { cloneDeep } from 'lodash'
import { NumericConstants } from '@/utils/constants'
import { DialogWidth } from '@/utils/Dialog'

export default {
  name: 'DeckForm',

  props: {
    propDeck: {
      type: Object,
      default: undefined
    },
    location: {
      type: Object,
      required: true
    },
    width: {
      type: String,
      default: DialogWidth.SimpleWide
    },
    height: {
      type: String,
      default: undefined
    }
  },

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    MoneyTextField: () => import('@/components/helper/MoneyTextField.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    DeckProductSelector: () => import('@/components/settings-components/decks/DeckProductSelector.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    SpeciesAutocomplete: () => import('@/components/settings-components/product-settings/species/SpeciesAutocomplete.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  data: () => ({
    NumericConstants,
    rules: fieldRules.rules,
    deck: {
      name: '',
      status: true,
      value: 0,
      onDeck: 0,
      locationId: -1,
      contentType: 0,
      consumptionMode: 0,
      isDefault: false,
      deckContents: [],
      productIds: [],
      speciesId: undefined
    },
    speciesFetched: false,
    initialSpeciesSelection: null,
    renderSpeciesAutocomplete: false
  }),

  computed: {
    ...mapGetters('species', ['allSpecies', 'speciesLoading']),

    title () {
      if (this.propDeck) {
        return this.$t('editingDeck', { deckName: this.propDeck.name })
      } else {
        return this.$t('creatingDeck')
      }
    },

    isByproductContentType () {
      return this.deck.contentType === DeckContentType.Byproducts.name
    },

    isLogYardSaleContentType () {
      return this.deck.contentType === DeckContentType.LogYardSale.name
    },

    isEditing () {
      return this.propDeck !== undefined
    },

    logYardSaleDeckExists () {
      return this.location.decks.some(d => d.contentType === DeckContentType.LogYardSale.value && d.status !== DeckStatus.Archived.value)
    },

    contentTypes () {
      return DeckContentType.names.map(name => ({
        name,
        logYardSaleUnavailable: name === DeckContentType.LogYardSale.name && !this.isEditing && this.logYardSaleDeckExists
      }))
    },

    consumptionModesAvailable () {
      if (this.isLogYardSaleContentType) {
        return [ConsumptionMode.WeightBased.name]
      }
      if (this.isByproductContentType) {
        return ConsumptionMode.names.filter(n => ![ConsumptionMode.BoardFoot.name, ConsumptionMode.TicketBased.name].includes(n))
      } else {
        return ConsumptionMode.names
      }
    },

    showSpecies () {
      return this.deck.consumptionMode === ConsumptionMode.BoardFoot.name
    },

    filteredSpecies () {
      return this.allSpecies.filter(s => {
        return this.location.decks.filter(d => d.deckId !== this.propDeck?.deckId).every(d => d.speciesId !== s.speciesId) // TODO MK
      })
    },

    showProductRestrictionOptions () {
      return this.deck.consumptionMode !== ConsumptionMode.BoardFoot.name && !this.isLogYardSaleContentType
    },

    speciesAutocompleteHint () {
      return !this.isEditing &&
        this.allSpecies.every(s => this.location.decks.some(d => d.speciesId === s.speciesId)) ? this.$t('noSpeciesNotAssignedDeckAtLocation') : undefined
    },

    showDeckUnitAlert () {
      return !this.isEditing &&
        this.deck.consumptionMode === ConsumptionMode.BoardFoot.name &&
        this.allSpecies.every(s => this.location.decks.some(d => d.speciesId === s.speciesId)) ? this.$t('noSpeciesNotAssignedDeckAtLocation') : undefined
    },

    deckCanBeDefault () {
      return this.deck.consumptionMode !== ConsumptionMode.BoardFoot.name && !this.isLogYardSaleContentType
    },

    consumptionModeDisabled () {
      return this.propDeck?.consumptionMode === ConsumptionMode.BoardFoot.value || this.isLogYardSaleContentType
    },

    onDeckLabel () {
      return this.deck.consumptionMode === ConsumptionMode.BoardFoot.name
        ? this.$t('onDeckMbf')
        : this.$t('onDeckTons')
    },

    isBoardFootConsumptionMode () {
      return this.deck.consumptionMode === ConsumptionMode.BoardFoot.name || this.deck.consumptionMode === ConsumptionMode.BoardFoot.value
    },

    consumptionModeHint () {
      return this.deck.onDeck > 0 ? this.isBoardFootConsumptionMode ? this.$t('deckAlreadyHasVolume') : this.$t('deckAlreadyHasWeight') : ''
    },

    onDeckMultiplier () {
      return this.isBoardFootConsumptionMode ? NumericConstants.MbfDivisor : NumericConstants.ShortTonDivisor
    }
  },

  watch: {
    isByproductContentType (value) {
      if (!value) return
      this.deck.consumptionMode = ConsumptionMode.WeightBased.name
    },

    isLogYardSaleContentType (value) {
      if (!value) return
      this.deck.consumptionMode = ConsumptionMode.WeightBased.name
    }
  },

  async created () {
    if (this.propDeck) {
      this.deck = cloneDeep(this.propDeck)
      this.deck.onDeck = parseFloat((this.deck.onDeck / this.onDeckMultiplier).toFixed(3))
      this.deck.status = this.deck.status === 0
      this.deck.consumptionMode = ConsumptionMode.fromInt(this.deck.consumptionMode)
      this.deck.contentType = DeckContentType.fromInt(this.deck.contentType)
      this.deck.deckContents = cloneDeep(this.propDeck.deckContents)
      this.initialSpeciesSelection = this.allSpecies.find(s => s.speciesId === this.deck.speciesId) || null
    } else {
      this.deck.consumptionMode = ConsumptionMode.fromInt(this.location.consumptionMode)
      this.deck.contentType = DeckContentType.Logs.name
    }
    await this.handleConsumptionModeChanged(this.deck.consumptionMode)
    this.renderSpeciesAutocomplete = true
  },

  mounted () {
    setTimeout(_ => {
      this.$refs.firstField.focus()
    }, 0)
  },

  methods: {
    ...mapActions('decks', ['createDeck', 'updateDeck', 'fetchDeckWithId']),
    ...mapActions('species', ['fetchSpecies']),

    deckValueEntered (value) { this.deck.value = value },

    productsSelected (products) {
      this.deck.productIds = products.map(p => p.productId)
    },

    deckProductIds () {
      return this.deck.deckContents.map(dc => dc.productId)
    },

    async saveChanges () {
      if (!this.validateDeck()) {
        return
      }

      const requestObj = cloneDeep(this.deck)
      requestObj.status = requestObj.status ? 0 : 1
      requestObj.locationId = this.location.locationId
      requestObj.value = parseFloat(this.deck.value)
      requestObj.onDeck = Math.round(parseFloat(this.deck.onDeck) * this.onDeckMultiplier)
      requestObj.contentType = DeckContentType.toInt(this.deck.contentType)
      requestObj.consumptionMode = ConsumptionMode.toInt(this.deck.consumptionMode)
      requestObj.productIds = this.deck.productIds ?? this.deck.deckContents.map(dc => dc.productId)
      requestObj.isDefault = this.deck.isDefault
      requestObj.deckContents = []
      requestObj.speciesId = this.deck.consumptionMode === ConsumptionMode.BoardFoot.name ? this.deck.speciesId : undefined

      if (this.propDeck) {
        await this.updateDeck(requestObj)
        this.$emit('deck-mutated', requestObj)
      } else {
        await this.createDeck(requestObj)
        this.$emit('deck-mutated', requestObj)
      }
    },

    closeForm () {
      this.$emit('close')
    },

    validateDeck () {
      const { name } = this.deck
      if (name.trim() === '') {
        this.setSnackError(this.$t('deckNameError'))
        return false
      }
      if (this.deck.productIds !== undefined && this.deck.productIds.length > 0 && this.deck.isDefault) {
        this.setSnackError(this.$t('restrictedDefaultDeckError'))
        return false
      }
      if (this.deck.consumptionMode === ConsumptionMode.BoardFoot.name && !this.deck.speciesId) {
        this.setSnackError(this.$t('boardFootDeckMustHaveSpecies'))
        return false
      }

      return true
    },

    async handleConsumptionModeChanged (cm) {
      if (cm === ConsumptionMode.BoardFoot.name) {
        if (!this.speciesFetched) {
          this.speciesFetched = true
          await this.fetchSpecies()
        }
        this.deck.isDefault = false
      } else if (cm !== ConsumptionMode.BoardFoot.name) {
        this.deck.speciesId = undefined
      }
    },

    speciesChosen (species) {
      this.deck.speciesId = species.speciesId
    }
  }
}
</script>
